<template>
  <div>
    <c-title>Bankverbindung</c-title>

    <c-busy v-if="loadStatus === $config.status.LOADING" />
    <template v-else>

      <p>
        Aktuell bei uns hinterlegte Daten:<br>
        <strong>IBAN: </strong>{{ displayIban }}<br>
        <strong>BIC: </strong>{{ displayBic }}<br>
        <strong>Kontoinhaber: </strong>{{ displayAccountOwner }}<br>
      </p>

      <c-form
        required-fields
        @submit.prevent="submit"
      >
        <p>Bitte füllen Sie bei einzelnen Änderungen Ihrer Bankverbindung immer alle drei Felder erneut aus.</p>

        <p>Sollte bei uns ein Dauerauftrag für Sie eingerichtet sein, geben Sie uns bitte im Falle der Änderung Ihrer
        Bankverbindung zusätzlich darüber per Mail an
          <a href="mailto:info@claussen-simon-stiftung.de">info@claussen-simon-stiftung.de</a> Bescheid.<br>
        </p>

        <c-grid>

          <c-grid-item :span="8">
            <c-form-input
              id="iban"
              ref="iban"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.iban.required
              }, {
                message: 'Bitte geben Sie eine gültige IBAN ein.',
                show: !$v.iban.iban
              }]"
              :has-error="$v.iban.$error"
              :is-valid="!$v.iban.$invalid"
              v-model="iban"
              aria-required="true"
              label="IBAN *"
              maxlength="34"
              @change="$v.iban.$touch"
              @input="handleIbanUpdate"
            />
          </c-grid-item>

          <c-grid-item :span="4">
            <c-form-input
              id="bic"
              :errors="[{
                message: 'Dieses Feld ist erforderlich.',
                show: !$v.bic.required
              }, {
                message: 'Bitte geben Sie eine gültige BIC ein.',
                show: !$v.bic.bic
              }]"
              :has-error="$v.bic.$error"
              :is-valid="!$v.bic.$invalid"
              v-model="bic"
              aria-required="true"
              label="BIC (SWIFT-Code) *"
              maxlength="11"
              @change="$v.bic.$touch"
              @input="handleBicUpdate"
            />
          </c-grid-item>

          <c-form-input
            id="account-owner"
            :errors="[{
              message: 'Dieses Feld ist erforderlich.',
              show: !$v.accountOwner.required
            }]"
            :has-error="$v.accountOwner.$error"
            :is-valid="!$v.accountOwner.$invalid"
            v-model="accountOwner"
            aria-required="true"
            label="Kontoinhaber *"
            @input="$v.accountOwner.$touch"
          />

        </c-grid>

        <c-button :busy="saveStatus === $config.status.SAVING">
          Speichern
        </c-button>

      </c-form>
    </template>
  </div>
</template>

<script>
import CBusy from '@/components/CBusy'
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CFormInput from '@/components/form/CFormInput'
import CGrid from '@/components/CGrid'
import CGridItem from '@/components/CGridItem'
import CTitle from '@/components/CTitle'

import handleRequestError from '@/functions/handleRequestError'
import IBAN from 'iban'
import { required } from 'vuelidate/lib/validators'
import { bic, iban } from '@/functions/validators'

export default {
  name: 'VDashboardBankAccount',

  components: {
    CBusy,
    CButton,
    CForm,
    CFormInput,
    CGrid,
    CGridItem,
    CTitle
  },

  data () {
    return {
      loadStatus: this.$config.status.IDLE,
      saveStatus: this.$config.status.IDLE,
      iban: '',
      bic: '',
      accountOwner: '',
      displayIban: '',
      displayBic: '',
      displayAccountOwner: ''
    }
  },

  validations: {
    iban: {
      iban,
      required
    },
    bic: {
      bic,
      required
    },
    accountOwner: {
      required
    }
  },

  created () {
    this.loadStatus = this.$config.status.LOADING
    let noInformationText = 'keine Informationen hinterlegt'

    this.$api.get('user/banking/')
      .then(response => {
        if (response.data.iban && response.data.iban[0] === '*') {
          this.displayIban = response.data.iban
        } else {
          this.displayIban = noInformationText
        }

        this.displayBic = response.data.bic ? response.data.bic : noInformationText
        this.displayAccountOwner = response.data.owner ? response.data.owner : noInformationText

        this.loadStatus = this.$config.status.SUCCESS
      })
      .catch(error => {
        handleRequestError({
          error,
          fallback: 'Ihre Bankverbindung kann zurzeit leider nicht geladen werden. Bitte versuchen Sie es später erneut.'
        })

        this.$router.push({ name: 'dashboard' })
      })
  },

  methods: {
    submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$store.commit('notifications/add', {
          text: 'Bitte füllen Sie alle Felder korrekt aus.',
          type: 'error'
        })

        return
      }

      this.saveStatus = this.$config.status.SAVING

      this.$api.post('user/banking/', {
        iban: IBAN.electronicFormat(this.iban),
        bic: this.bic,
        owner: this.accountOwner
      })
        .then(response => {
          this.$store.commit('notifications/add', {
            text: 'Ihre Bankverbindung wurde erfolgreich geändert.',
            type: 'success'
          })
          this.$router.push({ name: 'dashboard' })
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihre Bankverbindung kann zurzeit leider nicht gespeichert werden. Bitte versuchen Sie es später erneut.'
          })

          this.saveStatus = this.$config.status.ERROR
        })
    },

    handleBicUpdate () {
      this.bic = this.bic.toUpperCase()
    },

    handleIbanUpdate (value, event) {
      if (value[0] === '*') {
        this.iban = ''
        return
      }

      const position = event.target.selectionStart
      const prevLength = this.iban.length

      this.iban = IBAN.printFormat(this.iban)

      this.$nextTick(() => {
        const newLength = this.iban.length

        // Move caret position if space is inserted or removed
        if (prevLength > newLength) {
          event.target.setSelectionRange(position - 1, position - 1)
        } else if (prevLength < newLength) {
          event.target.setSelectionRange(position + 1, position + 1)
        }
      })
    }
  }
}
</script>
